import { Signal } from "typed-signals";
import { services } from "onearm";
import { SpinSpeedHUDContolls, SpinSpeedTypes } from "onearm";
import { TopInfoController } from "../controllers/TopInfoController";
import { ReelsFrameController } from "../controllers/ReelsFrameController";
import {
    SpinButtonsController,
    SoundPopupController,
    BetSettingsController,
    HUDStateController,
    HUDAnimationsController,
    InfoDisplayController,
} from "./controllers/index.js";

/**
 * @class HUDController
 * @description Facade controller that brings HUD layout to life.
 * Aggregates all HUD sub-controllers and provides unified interface.
 */
export class HUDController {
    /**
     * @param {Object} scene - HUDScene instance with layout
     * @param {Object} options
     * @param {Object} options.currencyFormatter - Currency formatter service
     * @param {number} [options.balance=0] - Initial balance
     * @param {number} [options.bet=0] - Initial bet
     * @param {number} [options.volume=100] - Initial volume
     * @param {boolean} [options.muted=false] - Initial muted state
     * @param {string} [options.spinType="normal"] - Initial spin speed type
     * @param {number} [options.freeSpinsPrice=0] - Free spins price
     * @param {number} [options.freeSpinsCount=0] - Free spins count
     * @param {boolean} [options.doubleChanceEnabled=false] - Double chance enabled
     */
    constructor(scene, options = {}) {
        this.scene = scene;
        this.layout = scene.layout;
        this.options = options;
        this.currencyFormatter = options.currencyFormatter;

        this.initSignals();
        this.initControllers();
        this.wireSignals();
        this.setupInitialState(options);
    }

    initSignals() {
        this.onPressSpin = new Signal();
        this.onPressStop = new Signal();
        this.onPressPlus = new Signal();
        this.onPressMinus = new Signal();
        this.onPressInfo = new Signal();
        this.onPressSettings = new Signal();
        this.onPressBuyBonus = new Signal();
        this.onPressAutoplay = new Signal();
        this.onPressStopAutoplay = new Signal();
        this.onPressFullScreen = new Signal();
        this.onPressConfirmationOk = new Signal();
        this.onPressConfirmationCancel = new Signal();
        this.onChangeDoubleChanceToggle = new Signal();

        this.onVolumeChange = new Signal();
        this.onMutedChange = new Signal();
        this.onSpinSpeedChange = new Signal();

        this.onPressBetPlus = new Signal();
        this.onPressBetMinus = new Signal();
        this.onPressCoinValuePlus = new Signal();
        this.onPressCoinValueMinus = new Signal();
        this.onPressTotalBetPlus = new Signal();
        this.onPressTotalBetMinus = new Signal();
        this.onPressBetMax = new Signal();
        this.onPressCloseBetSettings = new Signal();
    }

    initControllers() {
        this.spinButtons = new SpinButtonsController(this.layout);

        this.soundPopup = new SoundPopupController(this.layout, {
            volume: this.options.volume,
            musicVolume: this.options.musicVolume,
            sfxVolume: this.options.sfxVolume,
            isSoundsMuted: this.options.isSoundsMuted,
            isMusicMuted: this.options.isMusicMuted,
            isSfxMuted: this.options.isSfxMuted,
            parent: this.scene.parent,
        });

        this.betSettings = new BetSettingsController(this.layout, {
            currencyFormatter: this.currencyFormatter,
        });

        this.hudState = new HUDStateController(this.layout);

        this.animations = new HUDAnimationsController(this.layout);

        this.infoDisplay = new InfoDisplayController(this.layout, {
            currencyFormatter: this.currencyFormatter,
            gameConfig: services.gameConfig,
        });


        const reelsFrames = this.layout.findAll("ReelsFrame");
        reelsFrames.forEach(frame => {
            frame.parentLayer = services.get("layers").get("underReels");
        });
        this.reelsFrame = new ReelsFrameController(reelsFrames);

        this.topInfo = new TopInfoController(this.layout.findAll("TopInfoContainer"));

        const initialSpinSpeedType = this.getSpinSpeedTypeFromString(this.options.spinType);
        this.spinSpeed = new SpinSpeedHUDContolls(this.layout, { type: initialSpinSpeedType });
    }

    setDoubleChance(value) {
        this.hudState.setDoubleChance(value);

        this.layout.forAll("DoubleChanceToggle", (toggle) => {
            toggle.setState(value);
        });
    }

    wireSignals() {
        this.spinButtons.onPressSpin.connect((v) => this.onPressSpin.emit(v));
        this.spinButtons.onPressStop.connect((v) => this.onPressStop.emit(v));

        this.soundPopup.onVolumeChange.connect((v) => this.onVolumeChange.emit(v));
        this.soundPopup.onMutedChange.connect((v) => this.onMutedChange.emit(v));

        this.betSettings.onPressPlus.connect((v) => this.onPressPlus.emit(v));
        this.betSettings.onPressMinus.connect((v) => this.onPressMinus.emit(v));
        this.betSettings.onPressBetPlus.connect((v) => this.onPressBetPlus.emit(v));
        this.betSettings.onPressBetMinus.connect((v) => this.onPressBetMinus.emit(v));
        this.betSettings.onPressCoinValuePlus.connect((v) => this.onPressCoinValuePlus.emit(v));
        this.betSettings.onPressCoinValueMinus.connect((v) => this.onPressCoinValueMinus.emit(v));
        this.betSettings.onPressTotalBetPlus.connect((v) => this.onPressTotalBetPlus.emit(v));
        this.betSettings.onPressTotalBetMinus.connect((v) => this.onPressTotalBetMinus.emit(v));
        this.betSettings.onPressBetMax.connect((v) => this.onPressBetMax.emit(v));
        this.betSettings.onPressClose.connect((v) => this.onPressCloseBetSettings.emit(v));

        this.hudState.onChangeDoubleChanceToggle.connect((v) => this.onChangeDoubleChanceToggle.emit(v));

        this.spinSpeed.onChange.connect((v) => this.onSpinSpeedChange.emit(v));

        this.connectLayoutButton("InfoButton", this.onPressInfo);
        this.connectLayoutButton("MenuButton", this.onPressSettings);
        this.connectLayoutButton("BuyBonusButton", this.onPressBuyBonus);
        this.connectLayoutButton("AutoPlayButton", this.onPressAutoplay);
        this.connectLayoutButton("StopAutoPlayButton", this.onPressStopAutoplay);
        this.connectLayoutButton("FullScreenButton", this.onPressFullScreen);
        this.connectLayoutButton("OkButton", this.onPressConfirmationOk);
        this.connectLayoutButton("CancelButton", this.onPressConfirmationCancel);
    }

    connectLayoutButton(name, signal) {
        this.layout.forAll(name, (btn) => {
            if (btn?.onPress) {
                btn.onPress.connect((v) => signal.emit(v));
            }
        });
    }

    setupInitialState(options) {
        this.setDoubleChance(options.doubleChanceEnabled ?? false);

        this.layout.forAll("BetInfoPopup", (popup) => {
            popup.visible = false;
            popup.alpha = 0;
        });

        this.infoDisplay.setBalance(options.balance ?? 0);
        this.infoDisplay.setBet(options.bet ?? 0);
        this.infoDisplay.showInfo("HOLD SPACE FOR TURBO SPIN");

        this.infoDisplay.setFreeSpins(options.freeSpinsCount ?? 0);
        this.infoDisplay.setSelectedPrice(options.freeSpinsPrice ?? 0);

        this.topInfo.startRotation(["GameLogo", "symbols_pay", "win_free_spins", "win_over"], 1000 * 10);
    }

    showTopGameLogo() {
        this.topInfo.showInfo("GameLogo");
    }

    /**
     * @param {string} spinType - Spin type string from DataModel (normal/quick/turbo)
     * @returns {string} SpinSpeedTypes constant
     */
    getSpinSpeedTypeFromString(spinType) {
        const typeMap = {
            "normal": SpinSpeedTypes.NORMAL,
            "quick": SpinSpeedTypes.QUICK,
            "turbo": SpinSpeedTypes.TURBO
        };
        return typeMap[spinType] || SpinSpeedTypes.NORMAL;
    }

    setSpinType(value) {
        this.spinSpeed.type = this.getSpinSpeedTypeFromString(value);
    }

    setSpinState() {
        this.spinButtons.setSpinState();
    }

    setStopState() {
        this.spinButtons.setStopState();
    }

    setIdleState() {
        this.hudState.setIdleState();
        this.spinSpeed.enabled = true;
    }

    setSpinningState() {
        this.hudState.setSpinningState();
        this.betSettings.hideBetSettings();
    }

    setFreeSpinsState() {
        this.hudState.setFreeSpinsState();
        this.reelsFrame?.showFrame("FreeGame");
        this.topInfo.showInfo("GameLogo");
    }

    setMainGameState() {
        this.hudState.setMainGameState();
        this.reelsFrame?.showFrame("MainGame");
        this.topInfo.startRotation(["GameLogo", "symbols_pay", "win_free_spins", "win_over"], 1000 * 10);
    }

    showTumbleWin() {
        this.topInfo.showInfo("TumbleWinContainer");
    }

    setTumbleWinValue(value) {
        const formattedValue = this.currencyFormatter.format(value);

        this.layout.forAll("tumbleWinValue", (text) => {
            text.text = formattedValue;
        });
    }


    setTumbleWinText(value) {
        this.layout.forAll("tumbleWinValue", (text) => {
            text.text = value;
        });
    }

    setBuyBonusState() {
        this.hudState.setBuyBonusState();
        this.spinSpeed.enabled = false;
    }

    hideBuyBonusPopup() {
        this.hudState.hideBuyBonusPopup();
    }

    showAutoplayButton() {
        this.hudState.showAutoplayButton();
    }

    showStopAutoplayButton() {
        this.hudState.showStopAutoplayButton();
    }

    toFreeSpinsModeAnimation() {
        return this.animations.toFreeSpinsMode();
    }

    toMainGameModeAnimation() {
        return this.animations.toMainGameMode();
    }

    setTotalBet(value) {
        this.infoDisplay.setBet(value);
        this.betSettings.setTotalBetValue(value);

        const formattedValue = this.currencyFormatter.format(value);

        this.layout.forAll("BetInfoPopup.content.value", (text) => {
            text.text = formattedValue;
        });

        this.layout.forAll("BetInfoPopup.content", (container) => {
            if (container.layout) {
                container.layout();
            }
        });
    }

    setDoubleChanceBet(value) {
        const formattedValue = this.currencyFormatter.format(value);

        this.layout.forAll("DoubleChancePortraitToggle.PriceText", (text) => {
            text.text = formattedValue;
        });
        this.layout.forAll("DoubleChanceToggle.PriceText", (text) => {
            text.text = formattedValue;
        });
    }

    setBalance(value) {
        this.infoDisplay.setBalance(value);
    }

    setWin(value) {
        this.infoDisplay.setWin(value);
    }

    showInfo(text) {
        this.infoDisplay.showInfo(text);
    }

    showWinInfo(parameters) {
        this.infoDisplay.showWinInfo(parameters);
    }

    setInfoText(text) {
        this.infoDisplay.setInfoText(text);
    }

    setPaysText(text) {
        this.infoDisplay.setPaysText(text);
    }

    clearPayInfo() {
        this.infoDisplay.clearPayInfo();
    }

    showPayInfo(pay) {
        return this.infoDisplay.showPayInfo(pay);
    }

    setFreeSpinsCounter(value) {
        this.hudState.setFreeSpinsCounter(value);
        this.infoDisplay.setFreeSpinsCounter(value);
    }

    setFreeSpins(value) {
        this.infoDisplay.setFreeSpins(value);
    }

    setSelectedPrice(price) {
        this.infoDisplay.setSelectedPrice(price);
    }

    showBetSettings() {
        this.betSettings.setVisibleBetsPopup(true);
    }

    hideBetSettings() {
        this.betSettings.hideBetSettings();
    }

    isBetSettingsVisible() {
        return this.betSettings.isVisible();
    }

    setBetValue(value) {
        this.betSettings.setBetValue(value);
    }

    setCoinValue(value) {
        this.betSettings.setCoinValue(value);
    }

    setTotalBetValue(value) {
        this.betSettings.setTotalBetValue(value);
    }

    hideAllControls() {
        this.hudState.hideAllControls();
    }

    /**
     * @description Gets global position of tumbleWinValue for fly animation target
     * @returns {{x: number, y: number}|null} Global position or null
     */
    getTumbleWinTargetPosition() {
        return this.infoDisplay.getTumbleWinTargetPosition();
    }

    destroy() {
        this.spinButtons?.destroy();
        this.soundPopup?.destroy();
        this.betSettings?.destroy();
        this.hudState?.destroy();
        this.animations?.destroy();
        this.infoDisplay?.destroy();
        this.reelsFrame?.destroy?.();
        this.topInfo?.destroy?.();
    }
}

